import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { BookOpenText, Brain, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/services/api";

const emptyGuide = {
  week_title: "",
  theme_pt: "",
  level_focus: "Básico",
  warmup_pt: "",
  conversation_prompts: "",
  correction_focus: "",
  homework_pt: "",
  is_active: true,
};

function listToText(list = []) {
  return list.join("\n");
}

function textToList(value) {
  return value.split("\n").map((item) => item.trim()).filter(Boolean);
}

function guideToForm(guide) {
  return {
    week_title: guide.week_title,
    theme_pt: guide.theme_pt,
    level_focus: guide.level_focus,
    warmup_pt: guide.warmup_pt,
    conversation_prompts: listToText(guide.conversation_prompts),
    correction_focus: listToText(guide.correction_focus),
    homework_pt: guide.homework_pt,
    is_active: guide.is_active,
  };
}

function formToPayload(form) {
  return {
    ...form,
    conversation_prompts: textToList(form.conversation_prompts),
    correction_focus: textToList(form.correction_focus),
  };
}

function GuideField({ field, form, label, onChange, textarea = false }) {
  const inputId = `teacher-guide-${field}-input`;
  const className = textarea ? "zero-textarea mt-2" : "auth-input mt-2";
  const value = form[field];
  const testId = `teacher-guide-${field}-input`;
  const handleChange = (event) => onChange(field, event.target.value);
  return (
    <div>
      <label className="zero-label" htmlFor={inputId} data-testid={`teacher-guide-${field}-label`}>{label}</label>
      {textarea ? (
        <textarea id={inputId} className={className} value={value} onChange={handleChange} data-testid={testId} />
      ) : (
        <input id={inputId} className={className} value={value} onChange={handleChange} data-testid={testId} />
      )}
    </div>
  );
}

function GuideForm({ editingGuide, error, form, isPending, onCancel, onChange, onSubmit }) {
  return (
    <form className="neo-card surface-neutral grid gap-4 p-5" onSubmit={onSubmit} data-testid="teacher-guide-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid="teacher-guide-form-kicker">{editingGuide ? "editar roteiro" : "novo roteiro"}</p>
          <h2 className="font-display text-3xl font-black" data-testid="teacher-guide-form-title">Dica de aula semanal</h2>
        </div>
        <button type="button" className="secondary-action w-auto" onClick={onCancel} data-testid="teacher-guide-cancel-button">Limpar</button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <GuideField field="week_title" form={form} label="Título da semana" onChange={onChange} />
        <GuideField field="level_focus" form={form} label="Foco de nível" onChange={onChange} />
      </div>
      <GuideField field="theme_pt" form={form} label="Tema da aula" onChange={onChange} />
      <GuideField field="warmup_pt" form={form} label="Aquecimento" onChange={onChange} textarea />
      <div className="grid gap-4 md:grid-cols-2">
        <GuideField field="conversation_prompts" form={form} label="Prompts de conversa — 1 por linha" onChange={onChange} textarea />
        <GuideField field="correction_focus" form={form} label="Foco de correção — 1 por linha" onChange={onChange} textarea />
      </div>
      <GuideField field="homework_pt" form={form} label="Tarefa para alunos" onChange={onChange} textarea />
      <label className="inline-flex items-center gap-3 text-sm font-black text-white" data-testid="teacher-guide-active-label">
        <input type="checkbox" checked={form.is_active} onChange={(event) => onChange("is_active", event.target.checked)} data-testid="teacher-guide-active-checkbox" />
        Mostrar como roteiro ativo
      </label>
      {error && <div className="auth-error" role="alert" data-testid="teacher-guide-error-alert">{error}</div>}
      <button className="primary-action w-auto" type="submit" disabled={isPending} data-testid="teacher-guide-save-button">
        <Save size={18} /> {isPending ? "Salvando..." : "Salvar guia"}
      </button>
    </form>
  );
}

function GuideCard({ guide, onDelete, onEdit }) {
  return (
    <article className="neo-card surface-neutral p-5" data-testid={`teacher-guide-card-${guide.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid={`teacher-guide-level-${guide.id}`}>{guide.level_focus}</p>
          <h2 className="mt-2 font-display text-3xl font-black" data-testid={`teacher-guide-title-${guide.id}`}>{guide.week_title}</h2>
          <p className="mt-2 text-sm font-bold text-white/70" data-testid={`teacher-guide-theme-${guide.id}`}>{guide.theme_pt}</p>
        </div>
        <span className="profile-badge" data-testid={`teacher-guide-status-${guide.id}`}>{guide.is_active ? "ativo" : "rascunho"}</span>
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid={`teacher-guide-content-${guide.id}`}>
        <div className="teacher-guide-block" data-testid={`teacher-guide-warmup-${guide.id}`}><strong>Aquecimento</strong><p>{guide.warmup_pt}</p></div>
        <div className="teacher-guide-block" data-testid={`teacher-guide-homework-${guide.id}`}><strong>Tarefa</strong><p>{guide.homework_pt}</p></div>
        <div className="teacher-guide-block" data-testid={`teacher-guide-prompts-${guide.id}`}><strong>Prompts</strong><ul>{guide.conversation_prompts.map((item) => <li key={item}>{item}</li>)}</ul></div>
        <div className="teacher-guide-block" data-testid={`teacher-guide-corrections-${guide.id}`}><strong>Correções</strong><ul>{guide.correction_focus.map((item) => <li key={item}>{item}</li>)}</ul></div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" className="secondary-action w-auto" onClick={() => onEdit(guide)} data-testid={`teacher-guide-edit-button-${guide.id}`}><Pencil size={18} /> Editar</button>
        <button type="button" className="secondary-action w-auto" onClick={() => onDelete(guide.id)} data-testid={`teacher-guide-delete-button-${guide.id}`}><Trash2 size={18} /> Apagar</button>
      </div>
    </article>
  );
}

function UnlockInsightsPanel({ insights }) {
  if (!insights) return <div className="neo-card surface-neutral p-5" data-testid="teacher-guide-unlock-insights-loading">Carregando insights do Modo Destravar...</div>;
  return (
    <section className="teacher-unlock-insights" data-testid="teacher-guide-unlock-insights">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid="teacher-unlock-insights-kicker">modo destravar</p>
          <h2 className="font-display text-3xl font-black" data-testid="teacher-unlock-insights-title">Ordem sugerida para aula ao vivo</h2>
        </div>
        <Brain size={30} />
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="teacher-unlock-insights-metrics">
        <div className="unlock-metric-card"><span>Ativos</span><strong data-testid="teacher-unlock-active-users">{insights.active_unlock_users}</strong></div>
        <div className="unlock-metric-card"><span>Atenção</span><strong data-testid="teacher-unlock-needs-attention">{insights.needs_attention_count}</strong></div>
        <div className="unlock-metric-card"><span>Alunos</span><strong data-testid="teacher-unlock-total-students">{insights.total_students}</strong></div>
      </div>
      <div className="mt-5 grid gap-3" data-testid="teacher-unlock-student-list">
        {insights.students.slice(0, 6).map((student, index) => (
          <article key={student.user_id} className="teacher-unlock-student-row" data-testid={`teacher-unlock-student-${student.user_id}`}>
            <strong data-testid={`teacher-unlock-student-name-${student.user_id}`}>{index + 1}. {student.name}</strong>
            <span data-testid={`teacher-unlock-student-block-${student.user_id}`}>{student.block_label_pt || "Sem diagnóstico"}</span>
            <small data-testid={`teacher-unlock-student-action-${student.user_id}`}>{student.suggested_live_prompt}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function countActiveGuides(guides) {
  return guides.reduce((total, guide) => total + (guide.is_active ? 1 : 0), 0);
}

export default function TeacherGuide() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyGuide);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const { data: guides = [], isLoading } = useQuery({ queryKey: ["teacher-guides"], queryFn: api.getTeacherGuides, enabled: user?.role === "admin" });
  const { data: unlockInsights } = useQuery({ queryKey: ["admin-unlock-insights"], queryFn: api.getAdminUnlockInsights, enabled: user?.role === "admin" });
  const activeGuideCount = countActiveGuides(guides);

  const saveMutation = useMutation({
    mutationFn: () => editingId ? api.updateTeacherGuide(editingId, formToPayload(form)) : api.createTeacherGuide(formToPayload(form)),
    onSuccess: () => {
      setForm(emptyGuide);
      setEditingId(null);
      setError("");
      queryClient.invalidateQueries({ queryKey: ["teacher-guides"] });
    },
    onError: (err) => setError(formatApiErrorDetail(err.response?.data?.detail)),
  });
  const deleteMutation = useMutation({
    mutationFn: api.deleteTeacherGuide,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["teacher-guides"] }),
    onError: (err) => setError(formatApiErrorDetail(err.response?.data?.detail)),
  });

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const editGuide = (guide) => {
    setEditingId(guide.id);
    setForm(guideToForm(guide));
    setError("");
  };
  const clearForm = () => {
    setEditingId(null);
    setForm(emptyGuide);
    setError("");
  };
  const submit = (event) => {
    event.preventDefault();
    saveMutation.mutate();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="teacher-guide-page">
      <section className="page-title-row" data-testid="teacher-guide-title-section">
        <p className="section-kicker" data-testid="teacher-guide-kicker">admin · guia do professor</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="teacher-guide-heading">Roteiros para as aulas semanais.</h1>
        <p className="max-w-3xl text-base font-semibold text-slate-600 md:text-lg" data-testid="teacher-guide-description">Crie, edite e organize dicas para conduzir a aula de conversação de domingo.</p>
      </section>
      <section className="mt-8 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]" data-testid="teacher-guide-layout">
        <div className="grid gap-5">
          <article className="neo-card surface-yellow p-5" data-testid="teacher-guide-summary-card">
            <BookOpenText size={30} />
            <h2 className="mt-3 font-display text-4xl font-black" data-testid="teacher-guide-summary-total">{guides.length}</h2>
            <p className="font-black text-slate-600" data-testid="teacher-guide-summary-copy">roteiros salvos · {activeGuideCount} ativos</p>
          </article>
          <UnlockInsightsPanel insights={unlockInsights} />
          <GuideForm editingGuide={editingId} error={error} form={form} isPending={saveMutation.isPending} onCancel={clearForm} onChange={updateField} onSubmit={submit} />
        </div>
        <section className="grid gap-5" data-testid="teacher-guide-list">
          {isLoading ? <div className="neo-card surface-neutral p-6" data-testid="teacher-guide-loading-state">Carregando guias...</div> : guides.map((guide) => <GuideCard key={guide.id} guide={guide} onDelete={(guideId) => deleteMutation.mutate(guideId)} onEdit={editGuide} />)}
          {!isLoading && guides.length === 0 && <div className="neo-card surface-neutral p-6" data-testid="teacher-guide-empty-state"><Plus size={22} /> Crie o primeiro roteiro semanal.</div>}
        </section>
      </section>
    </div>
  );
}